/* rule doesn't make sense for this file as the underscore dangle is an implementation detail of the React Table Library
    It must be used to access the rows and their indexes */
/* eslint-disable no-underscore-dangle */

/* Create Delete Update Table (CRUD) Component
  Uses React-Table V6: https://github.com/tannerlinsley/react-table/tree/v6
  Required Props:
    data: [{dataObject}] - data to be shown on the table. Exact data from this prop will be shown in the table
    columns: [{columObject}] - column object (see below) array in react-table format

  Optional Props:
    sortFunction: function(a,b) - Sort function to sort the data table. Default: undefined
    style: Object - CSS (JSON Format) to be applied to the graph.
    tableLoading: boolean - Display Table Loading boolean
    noDataText: string - Text to display when no rows are present
    defaultPageSize: number - Number of rows to display
    showPagination: boolean - Display pagination (default: false)
    getTrProps: function - getTrProps() from React-Table, format table rows
    options: Object - set of option parameters as well as CRUD functionality
      exportToCSV: boolean - Adds ExportToCSVComponent with CSV file containing exactly what the table displays.
                              Use exportToCSVOptions for exportToCSV parameters (check ExportToCSVComponent)
      exportToCSVOptions: Object - ExportToCSVComponent Options (check ExportToCSVComponent.js)
      deleteRowFunctionality: boolean - Enable Delete functionality, requires handleDeleteRowChange prop (see below)
                                    which acts as a callback
      selectRowsFunctionality: boolean - Enable Select functionality, requires handleSelectedChange prop (see below)
    SubComponent: function(row) - Subcomponent that takes row as a parameter. Row being the react-table object row.
    handleDeleteRowChange: function - callback function when user deletes a row.
                                  Required if deleteRowFunctionality is set to true.
                                  The callback will call handleDeleteRowChange(index)
                                      index: number; The index of dataObject being deleted
    handleUpdateCellChange: function - callback function when user edits a cell
                                Required if editable in a column (see below) is set to true
                                The callback will call handleUpdateCellChange(index, key, value)
                                  - index: number; The index of dataObject being edited
                                  - key: string; key which has been edited
                                  - value: string; New value from edit
                                  Ex: data[index][key] = value;
    handleSelectedChange: function - callback function when user selects rows
                                Required if selectRowsFunctionality is set to true.
                                The callback will call handleSelectedChange(selectedRows, selectedIndicesRows)
                                  - selectedRows is an array of rows selected in stack data structure,
                                  - selectedIndicesRows is an array of row indexe's selected in stack data structure,

  Terms:
    columnObject -
      Value: {
        Header: string,
        accessor: string,
        editable: boolean,
        filterable: boolean
        Cell: function(row)
      }
      Descriptions:
        Header - Represents what will be displayed in the header row of the table
        accessor - equates to the key value from dataObject
        editable - enables a column that can be edited
        filterable - enables filtering for that column
        Cell - Custom render of the cell, cannot be editable (will be overriden).

*/

import React, { Component } from 'react';
import ReactTable from 'react-table';
import './CRUDTable.css';
import { FaTrashO as Trash } from 'react-icons/lib/fa';
import { t } from 'i18next';
import ExportToCSVComponent from '../exportToCSV/ExportToCSVComponent';

class CRUDTableComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      columns: props.columns,
      data: props.data || [],
      dataTable: props.data || [],
      options: {
        deleteRowFunctionality: false,
        exportToCSV: props.exportToCSV || false,
        exportToCSVOptions: {
        },
        selectRowsFunctionality: false,
        ...props.options,
      },
      selectedRowsIndices: [],
      showPagination: props.showPagination || false,
      sortFunction: props.sortFunction || undefined,
      SubComponent: props.SubComponent,
      tableLoading: props.tableLoading || false,
    };
    this.setState = this.setState.bind(this);
    this.createTableColumns = this.createTableColumns.bind(this);
    this.handleDeleteRow = this.handleDeleteRow.bind(this);
    this.setDataTable = this.setDataTable.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);

    this.reactTable = React.createRef();
  }


  componentWillReceiveProps(props) {
    const { options } = props;
    if (! props.data) {
      this.setState({
        data: [],
        dataTable: [],
        options: {
          exportToCSVOptions: options ? options.exportToCSVOptions : null,
          ...options,
        },
        tableLoading: false,
      });
    } else {
      this.setState({
        data: props.data,
        dataTable: this.setDataTable(props.data),
        options: {
          exportToCSVOptions: options ? options.exportToCSVOptions : null,
          ...options,
        },
        tableLoading: props.tableLoading,
      });
    }
  }

  shouldComponentUpdate(props) {
    return props.data;
  }


  setDataTable(data) {
    const { sortFunction } = this.state;
    let result = [];
    if (data && data.length !== 0) {
      result = data.map((value, index) => ({
        ...value,
        __index: index,
      })).sort(sortFunction);
    } else {
      result = [];
    }
    return result;
  }

  handleSelectChange(row, event) {
    const { selectedRowIndices, data } = this.state;
    const { handleSelectedChange } = this.props;
    let selectedRowsIndicesCopy = [...selectedRowIndices];

    if (event.target.checked) {
      selectedRowsIndicesCopy.push(row.original.__index);
    } else {
      selectedRowsIndicesCopy = selectedRowsIndicesCopy.filter(
        selectedRowIndex => selectedRowIndex !== row.original.__index,
      );
    }

    const selectedRows = selectedRowsIndicesCopy.map(rowIndex => data[rowIndex]);

    this.setState({
      selectedRowsIndices: selectedRowsIndicesCopy,
    }, handleSelectedChange(selectedRows, selectedRowsIndicesCopy));
  }

  createTableColumns() {
    const {
      columns, options, selectedRowsIndices,
    } = this.state;
    const result = columns.map(column => (column.editable ? {
      Cell: row => (
        <div contentEditable
          onBlur={e => this.handleCellChange(e, row)}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: row.value,
          }}
        />
      ),
      ...column,
    } : column));

    if (options.selectRowsFunctionality) {
      const selectableColumn = {
        Cell: row => (
          <div className="actions">
            <input type="checkbox"
              checked={selectedRowsIndices.includes(row.original.__index)}
              onChange={event => this.handleSelectChange(row, event)}
            />
          </div>
        ),
        Header: t('tensorAnalytics.CRUDTableComponent.selectTableHeader'),
      };
      result.unshift(selectableColumn);
    }

    if (options.deleteRowFunctionality) {
      const actionColumn = {
        Cell: row => (
          <div className="actions">
            <Trash size={20}
              onClick={() => this.handleDeleteRow(row)}
            />
          </div>
        ),
        Header: t('tensorAnalytics.CRUDTableComponent.actionTableHeader'),
      };
      result.push(actionColumn);
    }

    return result;
  }

  handleDeleteRow(row) {
    const { data } = this.state;
    const { handleDeleteRowChange } = this.props;
    const dataCopy = [data];
    dataCopy.splice(row.original.__index, 1);
    this.setState({
      data: dataCopy,
    }, handleDeleteRowChange(row.original.__index));
  }

  handleCellChange(event, row) {
    const { data } = this.state;
    const { handleUpdateCellChange } = this.props;
    const dataCopy = data;
    dataCopy[row.original.__index][row.column.id] = event.target.innerHTML;
    this.setState({
      data: dataCopy,
    }, handleUpdateCellChange(row.original.__index, row.column.id, event.target.innerHTML));
  }

  render() {
    const {
      columns,
      dataTable,
      SubComponent,
      tableLoading,
      showPagination,
      options,
    } = this.state;
    const {
      defaultSorted, style, getTrProps, noDataText, defaultPageSize = 25
    } = this.props;
    return (
      <div className="table"
        ref={this.reactTable}
      >
        <ReactTable data={dataTable}
          defaultSorted={defaultSorted}
          className="-striped -highlight"
          columns={this.createTableColumns()}
          SubComponent={SubComponent}
          style={{
            ...style,
          }}
          loading={tableLoading}
          showPagination={showPagination}
          getTrProps={getTrProps}
          defaultPageSize={defaultPageSize}
          nextText={t('tensorAnalytics.common.next')}
          previousText={t('tensorAnalytics.common.previous')}
          loadingText={t('tensorAnalytics.CRUDTableComponent.loadingText')}
          noDataText={noDataText || t('tensorAnalytics.CRUDTableComponent.noDataText')}
          pageText={t('tensorAnalytics.CRUDTableComponent.pageText')}
          ofText={t('tensorAnalytics.CRUDTableComponent.ofText')}
          rowsText={t('tensorAnalytics.CRUDTableComponent.rowsText')}
          pageJumpText={t('tensorAnalytics.CRUDTableComponent.pageJumpText')}
          rowsSelectorText={t('tensorAnalytics.CRUDTableComponent.rowsSelectorText')}
          resizable={false}
        />
        {options.exportToCSV && (
          <ExportToCSVComponent data={dataTable}
            dataHeaders={columns.map(header => header.accessor)}
            options={{
              ...options.exportToCSVOptions,
            }}
          />
        )}
      </div>
    );
  }
}

export default CRUDTableComponent;
